import { auth } from "hooks/useFirebase";
import { getImpersonatedUserId } from "hooks/useImpersonation/useImpersonation";
import { logError, logApi, LogCategory, LogLevel } from "utils/sentryLogger";

// Helper function to check if error is a 5xx error
const is5xxError = (status) => {
  return status >= 500 && status < 600;
};

// Helper function to sleep for a given duration
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// A request with no deadline is an infinite spinner with no error and no
// telemetry. The deadline spans the whole retry sequence, but `sleep` is a bare
// setTimeout, so a deadline firing mid-backoff is only noticed when the next
// attempt starts — up to a second late.
const DEFAULT_TIMEOUT_MS = 90_000;

// Must stay a function: one signal per request. Hoisting the timeout to a
// module constant would share a single deadline that starts at module load.
// jsdom 16 (the pinned test env) has no AbortSignal.timeout, and neither do
// pre-2022 browsers; there, requests keep their previous no-deadline behavior.
const defaultSignal = () =>
  typeof AbortSignal?.timeout === "function" ? AbortSignal.timeout(DEFAULT_TIMEOUT_MS) : null;

// A timed-out request must not spend its remaining retries failing instantly
// against a dead signal. TimeoutError is what AbortSignal.timeout rejects with,
// AbortError is a caller-supplied abort, and the signal check catches any
// browser that reports a timeout under another name.
const isAborted = (error, signal) =>
  error?.name === "TimeoutError" || error?.name === "AbortError" || Boolean(signal?.aborted);

// Critical endpoints to log - keeps log volume manageable
const CRITICAL_ENDPOINTS = [
  { pattern: /^group\/[^/]+\/join/, name: 'group_join' },
  { pattern: /^group\/[^/]+\/settings(\?|$)/, name: 'group_settings' },
  { pattern: /^session\/[^/]+\/vote_to_start(\?|$)/, name: 'vote_to_start' },
  { pattern: /^session\/?(\?|$)/, name: 'session_fetch' },
  { pattern: /^session\/[^/]+\/task\/terminate$/, name: 'session_terminate' },
  { pattern: /^account\/educator$/, name: 'educator_account' },
  // Anchored so the per-row fan-out (session_history_solo_accuracy/{id}) and
  // session_history/students stay out of the log.
  { pattern: /^account\/session_history(\?|$)/, name: 'session_history' },
];

// Helper to check if endpoint is critical and get its name
const getCriticalEndpointName = (path) => {
  for (const { pattern, name } of CRITICAL_ENDPOINTS) {
    if (pattern.test(path)) return name;
  }
  return null;
};

// Helper function to perform fetch with retry logic for 5xx errors
const NO_CONTENT = 204;

// Every 204 is the answer, whatever its method or content type; its empty body
// is not read (reading it throws, and the throw would be retried).
const fetchWithRetry = async (url, data, maxRetries = 3, path = null, stats = null) => {
  const retryDelays = [250, 500, 1000]; // 250ms, 500ms, 1s
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Logged duration is the sum of every attempt, so the count is what makes
    // it readable: four 3s attempts is a different diagnosis from one 12s one.
    if (stats) stats.attempts = attempt + 1;
    try {
      const response = await fetch(url, data);
      const contentType = response.headers.get("content-type");

      if (!response.ok) {
        // If it's a 5xx error and we have retries left, retry — unless the
        // deadline passed while we were waiting, in which case fall through
        // and reject with the last status rather than sleeping again.
        if (is5xxError(response.status) && attempt < maxRetries && !isAborted(null, data?.signal)) {
          lastError = response;
          const delayMs = retryDelays[attempt];
          logApi("API retry attempt", {
            level: LogLevel.WARNING,
            attempt: attempt + 1,
            maxRetries,
            statusCode: response.status,
            delayMs,
            endpoint: path || url,
            reason: "server_error",
          });
          await sleep(delayMs);
          continue;
        }

        // Either not a 5xx error or no retries left, reject with error
        const errorData = contentType?.includes("application/json")
          ? await response.json()
          : await response.text();
        // Include status code in error for handling specific HTTP errors
        const errorWithStatus = typeof errorData === 'object'
          ? { ...errorData, status: response.status, retryCount: attempt }
          : { message: errorData, status: response.status, retryCount: attempt };
        return Promise.reject(errorWithStatus);
      }

      // Success
      if (response.status === NO_CONTENT) return response;
      return contentType?.includes("application/json") ? await response.json() : response;
    } catch (e) {
      // Network error or other fetch error
      lastError = e;
      if (!isAborted(e, data?.signal) && attempt < maxRetries) {
        const delayMs = retryDelays[attempt];
        logApi("API retry attempt", {
          level: LogLevel.WARNING,
          attempt: attempt + 1,
          maxRetries,
          delayMs,
          endpoint: url,
          reason: "network_error",
          errorMessage: e?.message,
        });
        await sleep(delayMs);
        continue;
      }
      if (e && typeof e === 'object') {
        Object.assign(e, { retryCount: attempt });
      }
      return Promise.reject(e);
    }
  }

  // Should not reach here, but just in case
  return Promise.reject(lastError);
};

export const request = async (
  path,
  { method = "POST", headers = {}, payload, skipAuth = false, sse = false, signal = null, maxRetries = 3, noImpersonation = false } = {}
) => {
  let token;
  let allHeaders = {
    accept: "application/json",
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning":"69420",
    ...headers,
  };

  if (!skipAuth) {
    if (!auth.currentUser) {
      console.error("useOkoApi Error: No authenticated user");
      return Promise.reject(new Error("User not authenticated"));
    }

    try {
      // Force token refresh only when necessary to avoid excessive refreshes
      token = await auth.currentUser.getIdToken(false);
    } catch (e) {
      console.error("useOkoApi Error getting token:", e);
      logError("Failed to get Firebase auth token", e, {
        category: LogCategory.AUTH,
        hasCurrentUser: !!auth.currentUser,
      });
      return Promise.reject(e);
    }

    if (!token) {
      console.error("useOkoApi Error: No token available");
      return Promise.reject(new Error("Authentication failed - no token available"));
    }

    allHeaders.Authorization = `Bearer ${token}`;

    if (!noImpersonation) {
      const impersonatedId = getImpersonatedUserId();
      if (impersonatedId && !allHeaders["X-Impersonate-User-Id"]) {
        allHeaders["X-Impersonate-User-Id"] = impersonatedId;
      }
    }
  }

  const endpoint = process.env.REACT_APP_OKOLABS_API_ENDPOINT;

  const data = {
    headers: allHeaders,
    method,
  };

  if (payload) data["body"] = JSON.stringify(payload);
  // No default deadline on a stream — it is meant to stay open, so a 90s
  // timeout would be a guillotine. Nothing sets `sse` today; this keeps the
  // first caller that does from inheriting one.
  const requestSignal = signal || (sse ? null : defaultSignal());
  if (requestSignal) data["signal"] = requestSignal;

  if (sse) {
    console.log("performing sse request")
    const response = await fetch(`${endpoint}/${path}`, data)
    console.log("response:", response);

    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
    while (true) {
      const {value, done} = await reader.read();
      if (done) break;
      console.log('Received', value);
    }
    return
  }

  // Check if this is a critical endpoint that needs logging
  const endpointName = getCriticalEndpointName(path);

  // Non-critical endpoints: zero overhead path
  if (!endpointName) {
    return fetchWithRetry(`${endpoint}/${path}`, data, maxRetries, path);
  }

  // Critical endpoints: track timing and log
  const startTime = performance.now();
  const stats = { attempts: 0 };
  try {
    const result = await fetchWithRetry(`${endpoint}/${path}`, data, maxRetries, path, stats);
    const duration = Math.round(performance.now() - startTime);
    logApi("API request completed", {
      endpoint: path,
      endpointName,
      method,
      duration,
      attempts: stats.attempts,
    });
    return result;
  } catch (error) {
    const duration = Math.round(performance.now() - startTime);
    const is4xx = error?.status >= 400 && error?.status < 500;
    logApi("API request failed", {
      level: is4xx ? LogLevel.WARNING : LogLevel.ERROR,
      endpoint: path,
      endpointName,
      method,
      statusCode: error?.status,
      duration,
      attempts: stats.attempts,
      errorName: error?.name,
      errorMessage: error?.message || error?.detail,
    });
    return Promise.reject(error);
  }
};

export const useOkoApi = () => {
  return { fetch: request };
};

